import React from 'react';
import { Button } from '@progress/kendo-react-buttons';
import CpsLoader from '../../common/CpsLoader';
import { LoadingGridPanel } from '../../grid/LoadingGridPanel';
import { ConfirmDialog } from '../../common/ConfirmDialog';
import { Field, Form, FormElement, FormRenderProps } from '@progress/kendo-react-form';
import { Dialog } from '@progress/kendo-react-dialogs';
import { Editor, EditorUtils } from '@progress/kendo-react-editor';
import ReportIssueBody from '../helpCenterManagement/ReportIssueBody';
import { useGlobalizationContext, useTranslation, useUiAdminConfigsContext } from '@cs-cms/state-management';
import {
  CpsReportIssueState,
  CPS_UI_ADMIN_CONFIG_TYPE,
  CpsConfirmDialogState,
  CPS_CONFIRM_DIALOG_STATE
} from '@cs-cms/types';
import { FormTextArea } from '../../form/fields/FormMultilineText';
import { FormRichTextEditorLazy } from '../../form/fields/FormRichTextEditorLazy';
import { FormSwitcher } from '../../form/fields/FormSwitcher';
import { FormTextBox } from '../../form/fields/FormTextBox';
import { maxLengthRichTextEditor } from '../../form/validators/FormValidator';

interface ReportIssueDialogState {
  visible: boolean;
  values?: Record<string, any>;
}

export const ReportIssueManagement: React.FC = () => {
  const [initialValues, setInitValues] = React.useState<Partial<CpsReportIssueState>>();
  const [dialogState, setDialogState] = React.useState<ReportIssueDialogState>({ visible: false });
  const { selectedLocale } = useGlobalizationContext();
  const isInitialized = React.useRef(false);
  const { translate } = useTranslation(selectedLocale);

  const {
    isLoading,
    uiAdminConfigState,
    upsertUiAdminConfigEntry,
    uiAdminConfigPreviewState,
    setUiAdminConfigPreviewState,
    refreshUiAdminConfigState
  } = useUiAdminConfigsContext();

  const editor = React.useRef<Editor>(null);

  React.useEffect(() => {
    if (isLoading || isInitialized.current) {
      return;
    }

    if (uiAdminConfigState.reportIssue) {
      const values: CpsReportIssueState = uiAdminConfigState.reportIssue as CpsReportIssueState;
      if (editor.current) {
        const view = editor.current.view;
        if (view && values.keyUsersContactsContent) {
          EditorUtils.setHtml(view, values.keyUsersContactsContent);
        }
      }
      setInitValues(values);
    } else {
      setInitValues({});
    }

    isInitialized.current = true;
  }, [isLoading, uiAdminConfigState.reportIssue]);

  const upsertReportIssueSection = (values: Record<string, any>) => {
    const body = JSON.stringify(values);
    upsertUiAdminConfigEntry(CPS_UI_ADMIN_CONFIG_TYPE.REPORT_ISSUE, body);

    if (setUiAdminConfigPreviewState && uiAdminConfigPreviewState?.reportIssue) {
      setUiAdminConfigPreviewState({
        ...uiAdminConfigPreviewState,
        [CPS_UI_ADMIN_CONFIG_TYPE.REPORT_ISSUE]: undefined
      });
    }

    refreshUiAdminConfigState(CPS_UI_ADMIN_CONFIG_TYPE.REPORT_ISSUE);
  };

  const handleSubmit = (values: Record<string, any>) => {
    if (values.enabled) {
      setDialogState({ visible: true, values });
    } else {
      upsertReportIssueSection(values);
    }
  };

  const handlePreview = (formRenderProps: FormRenderProps) => {
    if (setUiAdminConfigPreviewState) {
      const reportIssuePreviewState: CpsReportIssueState = {
        enabled: true,
        buttonLabel: formRenderProps.valueGetter('buttonLabel'),
        emailRecipient: formRenderProps.valueGetter('emailRecipient'),
        emailSubject: formRenderProps.valueGetter('emailSubject'),
        emailBody: formRenderProps.valueGetter('emailBody'),
        keyUsersContactsContent: formRenderProps.valueGetter('keyUsersContactsContent')
      };
      setUiAdminConfigPreviewState({
        ...uiAdminConfigPreviewState,
        [CPS_UI_ADMIN_CONFIG_TYPE.REPORT_ISSUE]: reportIssuePreviewState
      });
    }
  };

  const discardFormChanges = () => {
    if (setUiAdminConfigPreviewState) {
      setUiAdminConfigPreviewState({
        ...uiAdminConfigPreviewState,
        [CPS_UI_ADMIN_CONFIG_TYPE.REPORT_ISSUE]: undefined
      });
    }
  };

  const handleDialogConfirm = (confirmed: CpsConfirmDialogState) => {
    if (confirmed === CPS_CONFIRM_DIALOG_STATE.YES && dialogState.values) {
      upsertReportIssueSection(dialogState.values);
    }
    setDialogState({ visible: false });
  };

  if (!initialValues) {
    return <CpsLoader />;
  }

  const maxLengthMessage = translate('custom.common.validation.editorContentExceeded');

  return (
    <div>
      {!initialValues && <LoadingGridPanel />}
      <Form
        onSubmit={handleSubmit}
        initialValues={initialValues}
        render={(formRenderProps: FormRenderProps) => {
          return (
            <FormElement>
              <div>
                <Field name={'enabled'} component={FormSwitcher} label={translate('custom.common.enable')} />
                <h3>{translate('custom.settings.adminCenter.reportIssue.reportIssueButton')}</h3>
                <Field
                  label={translate('custom.settings.adminCenter.reportIssue.buttonLabel')}
                  name={'buttonLabel'}
                  component={FormTextBox}
                />
                <Field
                  label={translate('custom.settings.adminCenter.reportIssue.emailRecipient')}
                  name={'emailRecipient'}
                  component={FormTextBox}
                />
                <Field
                  label={translate('custom.settings.adminCenter.reportIssue.emailSubject')}
                  name={'emailSubject'}
                  component={FormTextBox}
                />
                <Field
                  label={translate('custom.settings.adminCenter.reportIssue.emailBody')}
                  name={'emailBody'}
                  component={FormTextArea}
                />
                <h3>{translate('custom.settings.adminCenter.reportIssue.keyUserContacts')}</h3>
                <Field
                  name={'keyUsersContactsContent'}
                  component={FormRichTextEditorLazy}
                  validator={(value: string) => maxLengthRichTextEditor(value, maxLengthMessage)}
                />
                <div className={'cpsFormButtons'}>
                  <Button
                    type={'button'}
                    themeColor={'secondary'}
                    fillMode="solid"
                    onClick={() => handlePreview(formRenderProps)}
                  >
                    {translate('custom.common.preview')}
                  </Button>
                  <Button themeColor="dark" disabled={!formRenderProps.modified || !formRenderProps.valid}>
                    {translate('custom.common.save')}
                  </Button>
                </div>
              </div>
            </FormElement>
          );
        }}
      />
      {(uiAdminConfigPreviewState.reportIssue as CpsReportIssueState)?.enabled && (
        <Dialog
          onClose={discardFormChanges}
          title={`${translate('custom.settings.adminCenter.reportIssue.title')} - ${translate('custom.common.preview')}`}
          closeIcon={true}
          width={'100%'}
          height={'100%'}
          contentStyle={{ padding: 0 }}
        >
          <ReportIssueBody
            className="previewReportIssue"
            reportIssueState={uiAdminConfigPreviewState.reportIssue as CpsReportIssueState}
          />
        </Dialog>
      )}
      {dialogState.visible && (
        <ConfirmDialog
          message={translate('custom.settings.adminCenter.reportIssue.confirmationMessage')}
          onConfirm={handleDialogConfirm}
        />
      )}
    </div>
  );
};
